import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ToastProvider';
import { errorCode, errorMessage } from '@/lib/errors';
import { plannedOrToday, queueItemDefaultAccount, useOperationId, useSettleFinanceBatch } from '@/lib/finance';
import { formatMinor, fromMinor, toMinor } from '@/lib/money';
import { batchNeedsSecondPress, isHighlighted, needsSecondPress, settleOutcome, type PlanComparison } from '@/lib/planDeviation';
import {
  batchBankMinor,
  batchBankPending,
  batchPlannedTotalMinor,
  batchTypedBank,
  canSubmitBatch,
  draftLineInputs,
  liveLines,
  shortfallMinor,
  staleFieldError,
  suggestionIsCurrent,
  targetComparison,
  targetKey,
  type ConflictRow,
  type DraftContext,
  type DraftEvent,
  type LineKind,
  type Money,
  type TargetKey,
} from '@/lib/settleDraft';
import { AttachmentList } from './AttachmentList';
import { ReceiptUploadButton } from './ReceiptUploadButton';
import { offPlanText, outcomeText, planLine } from './planOutcomeText';
import { DraftConflictPanel, DraftLinesEditor, DraftStalePanel, DraftSuggestionInfo } from './SettleDraftPanels';
import { useConfirmTwice } from './useConfirmTwice';
import { useSettleDraft } from './useSettleDraft';
import type {
  FinanceAccount,
  FinanceBatchRequest,
  FinanceBatchTarget,
  FinanceCategory,
  FinanceQueueItem,
  FinanceRule,
} from '@/api/generated/types.gen';

export interface BatchSettleProps {
  /** The selected queue rows (spec §8.4) — all must share one direction. */
  items: readonly FinanceQueueItem[];
  accounts: readonly FinanceAccount[];
  categories: readonly FinanceCategory[];
  /** The rules, for the account the rows are set up to go through (OH-203). */
  rules?: readonly FinanceRule[];
  businessTz?: string;
  onClose: () => void;
}

// An operator line's kinds follow the payment's direction (OH-212), the first being the default of a new line. What comes
// in is kept from — a Fee, the usual reason a payout lands short (the server suggests the shortfall line itself, OH-193
// §4.3, but a hand-made one is a Fee by default) — or has extra income; what goes out has expenses on top.
const INCOMING_KINDS: readonly LineKind[] = ['withheld', 'income'];
const OUTGOING_KINDS: readonly LineKind[] = ['expense'];

// The hint under «Bank amount» (OH-212): incoming, the amount the operator may type; outgoing, a read-only figure.
const INCOMING_BANK_HINT =
  'What the account ends up with: the payments less the lines. Type a lower amount and the difference becomes a fee.';
const OUTGOING_BANK_HINT = 'What leaves the account: the payments plus the lines.';

// The article a line may carry has to agree with its kind (movements.go, the
// same rule MovementForm's LineFields enforces) — withheld may carry any
// active article, income/expense may not cross into the opposite kind.
function categoriesFor(kind: LineKind, active: readonly FinanceCategory[]) {
  return active.filter((c) => {
    if (kind === 'income') return c.kind !== 'expense';
    if (kind === 'expense') return c.kind !== 'income';
    return true;
  });
}

/** A queue row as a batch target: its key, or null for a row with no settleable id (a provider fact never gets here). */
function keyOf(it: FinanceQueueItem): TargetKey | null {
  return it.target.expected_id || it.target.schedule_entry_id ? targetKey(it.target) : null;
}

/** A planned row (finance_expected: a rule's, a manual one, a request) — as opposed to a booking row (a payment-schedule entry). */
const isPlanned = (k: TargetKey) => k.startsWith('expected:');

// The hint over the amounts of a batch that holds a planned row; a booking row beside it adds the second sentence (§3.4).
const PLANNED_HINT = 'Each amount is what was actually paid for the row; the line under a row says what happens to its plan.';
const BOOKING_HINT = ' Booking rows are paid in full.';

// What the window compares for one row: the share typed for it against its plan.
type Comparison = NonNullable<ReturnType<typeof targetComparison>>;

function batchTarget(k: TargetKey, amountMinor: number): FinanceBatchTarget {
  return k.startsWith('expected:')
    ? { expected_id: k.slice('expected:'.length), amount_minor: amountMinor }
    : { schedule_entry_id: k.slice('schedule_entry:'.length), amount_minor: amountMinor };
}

/**
 * Confirm several queue rows as one payment (spec §8.4), filled from the
 * settle preview (OH-193 spec §4.4): the targets' shares in the account
 * currency (converted by the server — cross-currency included) and the
 * suggested lines (a rule's fee per target, or the batch's shortfall on the
 * channel's article) live in the settle draft (lib/settleDraft). The draft's
 * base goes out as `expect` (§4.5) and a plan or rate that moved since is a
 * 409 target_changed and a conflict on screen, never a silent restatement.
 *
 * What the operator types and what is derived (OH-212, the owner's model):
 *   - the row amounts are what was SENT — prefilled from the plan, changed only by the operator;
 *   - «Bank amount», under the lines, is what the account ENDS UP with: the payments with their lines — Income and Expense
 *     adding, Fee taking away. It is derived (batchBankMinor) and is the header the batch sends (`amount_minor`), so the
 *     equation balances by construction — there is no difference left to check;
 *   - a LOWER amount typed into it is a fee kept before the money arrived: one automatic Fee line, the gap between the bank
 *     amount derived without that line (the payments with every other line, a rule's fee lines included) and the typed
 *     amount, set at that moment. After that the bank amount is derived again, so a line the operator adds later moves only
 *     it, and so does a row edited later;
 *   - an amount ABOVE that derived amount has no fee to explain it, and neither has one on an account where the server
 *     suggests no shortfall (a fee rule): both are a problem, and the difference is added as a Fee line by hand.
 *
 * The automatic line is the server's own shortfall, so its article and blockers stay the server's. The server suggests
 * «plan − bank» while the operator's fee is «payments − bank», and the two differ whenever a row is off its plan or a line
 * is there; so the window asks with `planned_total − fee` (batchTypedBank) and the shortfall that comes back is exactly
 * the fee. Its amount is locked (lockedSources): an edited line would survive the next answer and part from the ask. The
 * ask is a draft bank the operator typed, so Confirm waits for its answer (batchBankPending) — a held answer is for
 * another ask — and the operator's text stays in the field while it is theirs (see `bankText`). The ask is absolute, so a
 * plan that moves under it would turn into fee: the fee asked for is held (`heldFeeMinor`) and «Keep my amounts» asks again for
 * the new plan less that fee, which leaves the move to show in the bank amount.
 *
 * This replaces OH-205 §3.5, which kept the shortfall out of a batch holding a planned row because the shares explain the
 * bank: the fee here is not «plan − bank» but the gap between what was typed as sent and what arrived, so it applies to
 * every incoming batch. An outgoing batch has no automatic fee (the server suggests a shortfall for an incoming one only):
 * its bank amount is what leaves the account — the payments plus the expense lines — and is read-only.
 *
 * Fully unmounts between opens (the page renders it as
 * `{batchOpen && <BatchSettle .../>}`), so a single useOperationId() call —
 * no formKey — already mints a fresh id per open and reuses it across
 * retries of the SAME open. A retry after a 409 target_changed reuses it
 * too: the server's claim on the id rolled back with that refusal.
 *
 * The rows may change while the window is open — the queue page drops a row
 * settled elsewhere from `items` — and the draft treats that as a change of
 * its target set (§4.4 p.3): amounts typed for the old set go stale.
 *
 * It confirms by fact (OH-205 spec §4): a planned row closes at the amount
 * typed for it. A share off its plan is highlighted, and when any share (or
 * the shortfall, against the rows' plans together) is more than 5% off, the
 * first press on Confirm only arms the button and the second sends the body
 * that was on screen.
 */
export function BatchSettle({ items, accounts, categories, rules = [], businessTz, onClose }: BatchSettleProps) {
  const { toast } = useToast();
  const [operationId] = useOperationId();
  const settle = useSettleFinanceBatch();
  const activeCategories = categories.filter((c) => c.is_active);

  const directions = new Set(items.map((it) => it.direction));
  const rows = items.flatMap((it) => {
    const k = keyOf(it);
    return k ? [{ key: k, item: it }] : [];
  });
  const keysJoined = rows.map((r) => r.key).join(',');

  // OH-203: the account every row is set up to go through (each row's rule default, else its
  // category's), when the rows agree on one and it is active; else, as before, the first account
  // holding every row's currency. The rows' own currency when they share one and the account holds it.
  const rowDefault = (it: FinanceQueueItem) => {
    const d = queueItemDefaultAccount(it, rules, categories);
    return [d.ruleDefault, d.categoryDefault].find((id) => id && accounts.some((a) => a.id === id && a.is_active));
  };
  const firstDefault = items[0] ? rowDefault(items[0]) : undefined;
  const sharedDefault =
    firstDefault && items.every((it) => rowDefault(it) === firstDefault) ? accounts.find((a) => a.id === firstDefault) : undefined;
  const preferredAccount =
    sharedDefault ?? accounts.find((a) => items.every((it) => a.currencies.includes(it.currency))) ?? accounts[0];
  const rowsCurrency = items.every((it) => it.currency === items[0]?.currency) ? items[0]?.currency : undefined;
  const [accountId, setAccountId] = useState(preferredAccount?.id ?? '');
  const account = accounts.find((a) => a.id === accountId);
  const [currency, setCurrency] = useState(
    rowsCurrency && preferredAccount?.currencies.includes(rowsCurrency) ? rowsCurrency : (preferredAccount?.currencies[0] ?? ''),
  );
  // OH-204: the latest of the rows' planned days, once it has come — as the single windows do (OH-201).
  const [occurredOn, setOccurredOn] = useState(() =>
    plannedOrToday(items.reduce<string | undefined>((latest, it) => (!latest || it.due_date > latest ? it.due_date : latest), undefined), businessTz),
  );
  const [receipts, setReceipts] = useState<{ id: string; name: string }[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const context = useMemo<DraftContext | null>(
    () =>
      accountId && currency && occurredOn && keysJoined
        ? { accountId, currency, occurredOn, targets: keysJoined.split(',') as TargetKey[] }
        : null,
    [accountId, currency, occurredOn, keysJoined],
  );
  // The hints over the amounts only (OH-212): a batch with a planned row takes the server's shortfall like any incoming one.
  const hasPlannedRow = rows.some((r) => isPlanned(r.key));
  const hasBookingRow = rows.some((r) => !isPlanned(r.key));
  const { state, dispatch, preview, refetchAfterConflict } = useSettleDraft({ context, initialSendsBank: true });

  // Every row seen in this open, by key: a conflict row can name a target that
  // has since left the batch (the base still holds it), and it is labelled too.
  const seen = useRef(new Map<TargetKey, FinanceQueueItem>());
  for (const r of rows) seen.current.set(r.key, r.item);
  const categoryName = (id: string) => categories.find((c) => c.id === id)?.name ?? id;
  const labelFor = (key: ConflictRow['key']) => {
    if (key.startsWith('rate:')) return `${key.slice('rate:'.length)} rate`;
    const it = seen.current.get(key as TargetKey);
    if (!it) return `Payment ${key.slice(key.indexOf(':') + 1)}`;
    const at = rows.findIndex((r) => r.key === key);
    // the same numbering as the «Amount for target N» fields below
    return at >= 0 ? `Target ${at + 1} · ${categoryName(it.category_id)}` : `${categoryName(it.category_id)} (no longer in this batch)`;
  };

  const draftCurrency = state.context.currency;
  const incoming = directions.size === 1 && directions.has('in');
  // OH-212: the bank amount is derived — the payments less the lines — and is the header the batch sends. Not before the
  // draft has a currency: with no targets yet «the payments» would read as an amount of zero.
  const derivedMinor = draftCurrency ? batchBankMinor(state) : null;
  const plannedTotalMinor = batchPlannedTotalMinor(state);
  const bankPending = batchBankPending(state);

  // What the operator typed into «Bank amount», while it is theirs: the field shows it instead of the derived amount, so
  // the text is not taken from under the cursor when the answer to it brings the fee line. It was typed under the inputs
  // of `inputsKey` and is theirs only for as long as those stand: the operator's later edits move only the bank amount (the
  // owner's rule), so a changed row amount, operator line, account, currency or date lets it go. Not the server's lines —
  // the automatic fee is the answer to this very text arriving, and the draft's leaving the stale state (X1) brings the
  // rule's fee lines in as a consequence of the bank typed — and not the draft's bank, which the text itself moves.
  const inputsKey = JSON.stringify([
    accountId,
    currency,
    occurredOn,
    [...state.context.targets].sort().map((k) => [k, state.targetAmounts[k]?.major ?? null]),
    state.lines.filter((l) => l.source === 'operator').map((l) => [l.key, l.kind, l.categoryId, l.amount.major]),
  ]);
  const [typedBank, setTypedBank] = useState<{ text: string; inputs: string; unreadable?: boolean } | null>(null);
  const bankText = typedBank !== null && typedBank.inputs === inputsKey ? typedBank.text : null;
  // An entry the number field cannot read («2876,06» under an en locale): the browser reports "" for it, as for an emptied
  // field, and says so only in validity.badInput. It is not «no fee» but an entry nobody can ask for: it is named a problem
  // (Confirm is closed by that), nothing is asked, and it is held like any text until the operator fixes it or moves on.
  const bankUnreadable = typedBank !== null && typedBank.inputs === inputsKey && typedBank.unreadable === true;
  // MoneyInput does not forward focus events; the wrapper around the field listens for them.
  const [bankFocused, setBankFocused] = useState(false);
  const bankManual = state.bank.manual;

  // The fee the operator's typed bank asked for (OH-212). The draft's bank is the ASK, the absolute `planned_total − fee` of
  // the moment of typing. Kept across a plan that moved («Keep my amounts», K1) it would make the server's shortfall
  // `fee + Δplan` and let the fee swallow the move; so the fee is held, and Keep asks again for `new plan − fee` (see
  // `resolve`): the automatic fee stays what the operator typed, the rows and lines follow the plan, and the move shows in
  // the bank amount, where it is compared with the statement. It belongs to the draft's bank while that is the operator's
  // typed ask, and goes when the bank does: replaced by the next ask (a new typing; the field cleared is a fee of zero) or
  // discarded (Apply, Reset to suggestion, in the effect below). A bank gone stale (account, currency or rows changed) has
  // no conflict to Keep until it is typed again, which replaces the fee; a date alone (C2) keeps the bank, and the fee.
  const [heldFeeMinor, setHeldFeeMinor] = useState<number | null>(null);

  // The text goes with the typed bank when the draft discards it (Apply, Reset to suggestion): the bank was the operator's
  // and no longer is, and the fee it asked for with it. Only that change — a text nobody could ask for (zero, unreadable)
  // never made the bank theirs, and it stays, with its problem, until the operator fixes it or changes something else.
  const bankWasManual = useRef(false);
  useEffect(() => {
    if (bankWasManual.current && !bankManual) {
      setTypedBank(null);
      setHeldFeeMinor(null);
    }
    bankWasManual.current = bankManual;
  }, [bankManual]);
  // …when another input changed, and does not come back to life when the inputs return to what they were.
  useEffect(() => {
    setTypedBank((t) => (t !== null && t.inputs !== inputsKey ? null : t));
  }, [inputsKey]);
  // …and once the operator has left the field, when the text has done its job: nothing is typed in it, or the answer to it
  // has landed and the derived amount is what was typed (the field then shows that amount, formatted as the others are).
  // An unreadable entry has not: the field still shows it, and taking the record would put the derived amount over it.
  useEffect(() => {
    if (bankText === null || bankFocused || bankUnreadable) return;
    const typed = draftCurrency ? toMinor(bankText, draftCurrency) : null;
    if (bankText.trim() === '' || (!bankPending && typed !== null && typed === derivedMinor)) setTypedBank(null);
  }, [bankText, bankFocused, bankUnreadable, bankPending, derivedMinor, draftCurrency]);

  // An ask: the draft's bank becomes the amount to ask the server for, and the fee that ask stands for is remembered.
  const askBank = (askMinor: number, feeMinor: number) => {
    dispatch({ type: 'editBank', major: fromMinor(askMinor, draftCurrency) });
    setHeldFeeMinor(feeMinor);
  };
  // The operator types the bank amount the account ends up with; what is asked of the server is `planned_total − fee`
  // (batchTypedBank), so the shortfall that comes back is the fee. Emptying the field asks for no fee: the plan itself.
  // A text that is not an amount above zero (zero, a sign, too many decimals) is asked for nothing; its problem says so.
  const bankBadInput = useRef(false);
  const onBankChange = (major: string) => {
    if (bankBadInput.current) return; // "" for an entry the control cannot read, not an emptied field: readBankInput has named it
    setTypedBank({ text: major, inputs: inputsKey });
    if (!draftCurrency) return;
    if (major.trim() === '') {
      if (plannedTotalMinor !== null) askBank(plannedTotalMinor, 0);
      return;
    }
    const typed = toMinor(major, draftCurrency);
    const ask = typed !== null && typed > 0 ? batchTypedBank(state, typed) : null;
    if (ask) askBank(ask.arrivalMinor, ask.feeMinor);
  };
  // Read on every input and change event that reaches the field: while the reported value stays "" React fires no change
  // event at all (an entry made unreadable in an emptied field), and the input event is all there is. Runs before onChange.
  const readBankInput = (e: React.SyntheticEvent) => {
    const input = e.target as HTMLInputElement;
    const bad = input.validity?.badInput === true;
    bankBadInput.current = bad;
    if (bad) {
      setTypedBank((t) => (t?.unreadable && t.inputs === inputsKey ? t : { text: '', inputs: inputsKey, unreadable: true }));
    } else if (bankUnreadable && input.value === '') {
      onBankChange(''); // the unreadable entry deleted: an emptied field, and React saw no change to tell it
    }
  };

  const problems: string[] = [];
  if (directions.size > 1) problems.push('Every selected row must be the same direction (in or out).');
  if (!accountId || !currency) problems.push('Choose an account and a currency.');
  if (!occurredOn) problems.push('Choose the date the money moved.');
  const targetMinors = state.context.targets.map((k) => (currency ? toMinor(state.targetAmounts[k]?.major ?? '', currency) : null));
  if (targetMinors.some((m) => m === null || m <= 0)) problems.push('Every target needs an amount above zero.');
  liveLines(state).forEach((l, i) => {
    const amt = currency ? toMinor(l.amount.major, currency) : null;
    if (amt === null || amt <= 0) problems.push(`Line ${i + 1} needs an amount above zero.`);
    if (!l.categoryId) problems.push(`Line ${i + 1} needs an article.`);
  });
  if (incoming && bankUnreadable) problems.push(`Enter the bank amount, in ${currency}.`);
  // What the operator typed into the bank amount (OH-212), once the answer to it has landed: above the payments less the
  // lines there is no fee to explain it, and below them the server suggested none (a fee rule, an unusable article). While
  // the answer is awaited nothing is known yet, and Confirm is closed by canSubmitBatch.
  if (incoming && bankText !== null && bankText.trim() !== '' && draftCurrency) {
    const typed = toMinor(bankText, draftCurrency);
    if (typed === null || typed <= 0) {
      problems.push(`Enter the bank amount, in ${currency}.`);
    } else if (!bankPending && derivedMinor !== null && typed > derivedMinor) {
      problems.push(`The bank amount is ${formatMinor(typed - derivedMinor, currency)} more than the payments less the lines.`);
    } else if (!bankPending && derivedMinor !== null && typed < derivedMinor) {
      problems.push(`The difference of ${formatMinor(derivedMinor - typed, currency)} can’t become a fee here — add it as a Fee line.`);
    }
  }
  if (derivedMinor !== null && derivedMinor <= 0) problems.push('The bank amount comes out at zero or below — check the amounts.');
  const canConfirm = problems.length === 0 && canSubmitBatch(state);

  // OH-205 (spec §4, §5; plan decisions 5, 9): each share against its own plan, in the server's arithmetic
  // (targetComparison) — what the row highlights and what the second press reads. Everything is read as `submit` reads
  // it, from the draft's own context — its targets, its currency, its base — and not from the form's pickers, so the
  // comparison, the body and the body's fingerprint are one set of numbers. Nothing is compared while the draft is in
  // a conflict, stale or still empty (its plan and its base are not one answer), nor for a share of zero or below (the
  // problem list names it). `draftReady` starts with that same status, so whenever Confirm is enabled every share has
  // its comparison and the second press cannot be skipped.
  // A share as the draft holds it, in its own currency (only asked of a draft that has one: clean, or ready).
  const shareMinor = (k: TargetKey) => toMinor(state.targetAmounts[k]?.major ?? '', draftCurrency);
  const comparisons = new Map<TargetKey, Comparison>();
  if (state.status === 'clean') {
    for (const k of state.context.targets) {
      const paid = shareMinor(k);
      const c = paid !== null && paid > 0 ? targetComparison(state, k, paid) : null;
      if (c) comparisons.set(k, c);
    }
  }
  // The part of the plan the bank is asked to have kept: the draft's shortfall lines (an operator's own line is not one).
  // A line that does not read counts as none — Confirm is closed then, and nothing is armed.
  const shortfall = shortfallMinor(state) ?? 0;

  // What the operator reads before Confirm (OH-212): the plan against what was paid, and where the rest went — the fees
  // split into what the bank kept before the money arrived (the server's lines: the shortfall, a rule's fee) and what the
  // operator charged separately, then the operator's income and expense lines. In the account's currency; only what is known
  // and above zero is said, and a figure typed for another currency is not one of them.
  const summaryLines: string[] = [];
  if (draftCurrency) {
    const minorIn = (m: Money | undefined) => (m && m.currency === draftCurrency ? toMinor(m.major, draftCurrency) : null);
    const amount = (minor: number) => formatMinor(minor, draftCurrency);
    const paid = state.context.targets.map((k) => minorIn(state.targetAmounts[k]));
    if (plannedTotalMinor !== null && paid.length > 0 && paid.every((v): v is number => v !== null)) {
      summaryLines.push(`Plan ${amount(plannedTotalMinor)} · paid ${amount(paid.reduce((sum, v) => sum + v, 0))}`);
    }
    let kept = 0;
    let separate = 0;
    let income = 0;
    let expenses = 0;
    for (const l of liveLines(state)) {
      const v = minorIn(l.amount) ?? 0;
      if (l.kind === 'withheld') {
        if (l.source === 'operator') separate += v;
        else kept += v;
      } else if (l.kind === 'income') income += v;
      else expenses += v;
    }
    if (kept + separate > 0) {
      const parts = [
        ...(kept > 0 ? [`${amount(kept)} kept before it arrived`] : []),
        ...(separate > 0 ? [`${amount(separate)} charged separately`] : []),
      ];
      summaryLines.push(`Fees ${amount(kept + separate)}: ${parts.join(' · ')}`);
    }
    if (income > 0) summaryLines.push(`Income ${amount(income)}`);
    if (expenses > 0) summaryLines.push(`Expenses ${amount(expenses)}`);
  }

  // The body Confirm would send, built once per render — the second press compares its fingerprint, submit sends it.
  // Only a batch that can be confirmed has one; `canConfirm` needs a ready draft, so no fingerprint exists otherwise.
  let content: Omit<FinanceBatchRequest, 'operation_id'> | null = null;
  if (canConfirm && state.base) {
    const amountMinor = batchBankMinor(state); // derived: the payments less the lines (OH-212)
    const targets = state.context.targets.map((k) => {
      const share = shareMinor(k);
      return share === null ? null : batchTarget(k, share);
    });
    if (amountMinor !== null && targets.every((t): t is FinanceBatchTarget => t !== null)) {
      content = {
        targets,
        account_id: state.context.accountId,
        currency: draftCurrency,
        amount_minor: amountMinor,
        occurred_on: state.context.occurredOn,
        lines: draftLineInputs(state),
        attachment_ids: receipts.map((r) => r.id),
        expect: state.base.expect,
      };
    }
  }
  // §4, F2: more than 5% off the plan — a share, or the batch's shortfall against the rows' plans together — and the
  // first press only arms the button. `needs` reads the shares (the body's targets) and the shortfall lines (its lines);
  // the plans and rates they are compared with are a function of `expect`, which is in the body. A line's `source` is not
  // in the body, and that cannot skip a press: swapping a shortfall line for an operator line takes a remove and an add,
  // two fingerprint changes.
  const rowComparisons = [...comparisons.values()];
  const needs = rowComparisons.length > 0 && batchNeedsSecondPress(rowComparisons, shortfall);
  // What the armed button names: the worse of two numbers (spec §4) — the worst row that presses, and the shortfall's
  // share of the rows' plans together, which is the batch as one comparison (the plans against what is left of them after
  // the shortfall). Each joins whenever it presses by itself, so the list is never empty while `needs` holds.
  const pressingRows = rowComparisons.filter(needsSecondPress);
  const plannedTotal = rowComparisons.reduce((sum, c) => sum + c.plannedMinor, 0);
  const batchAsOne: PlanComparison = {
    paidMinor: plannedTotal - shortfall,
    plannedMinor: plannedTotal,
    crossCurrency: false,
    atPlan: false,
  };
  const pressing = needsSecondPress(batchAsOne) ? [...pressingRows, batchAsOne] : pressingRows;
  const twice = useConfirmTwice(content && JSON.stringify(content), needs);

  // Keep / Apply / Reset settle the question the 409 message pointed at. «Keep my amounts» on a bank the operator typed asks
  // again, for the new plan less the fee held (heldFeeMinor): until that answer lands the draft's bank is a new ask and Confirm
  // waits (batchBankPending). Only Keep, and only out of a conflict over a current answer — the one K1 takes — so nothing is
  // asked while the draft is stale or still in conflict.
  const resolve = (e: DraftEvent) => {
    setError(null);
    dispatch(e);
    if (
      e.type === 'keepMyAmounts' &&
      state.status === 'conflict' &&
      heldFeeMinor !== null &&
      plannedTotalMinor !== null &&
      plannedTotalMinor > heldFeeMinor
    ) {
      askBank(plannedTotalMinor - heldFeeMinor, heldFeeMinor);
    }
  };

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!content || !twice.press()) return;
    settle.mutate(
      { operation_id: operationId, ...content },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: `${state.context.targets.length} payments confirmed as one` });
          onClose();
        },
        onError: (err) => {
          if (errorCode(err) === 'target_changed') {
            // a plan or a rate moved since the suggestion: ask again, the draft goes into a conflict (S3)
            refetchAfterConflict();
            setError('A plan or an exchange rate changed since the suggestion — compare them below.');
          } else setError(errorMessage(err));
        },
      },
    );
  }

  return (
    <Dialog open onClose={onClose} side title={`Confirm ${rows.length} as one payment`}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Account"
            value={accountId}
            options={accounts.map((a) => ({ value: a.id, label: a.name }))}
            onChange={(e) => {
              const next = accounts.find((a) => a.id === e.target.value);
              setAccountId(e.target.value);
              setCurrency(next?.currencies[0] ?? '');
            }}
          />
          <Select
            label="Currency"
            value={currency}
            options={(account?.currencies ?? []).map((c) => ({ value: c, label: c }))}
            onChange={(e) => setCurrency(e.target.value)}
          />
          <Input label="Date" type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} required />
        </div>

        <DraftConflictPanel state={state} dispatch={resolve} labelFor={labelFor} />
        <DraftStalePanel state={state} dispatch={resolve} />

        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Targets</legend>
          {hasPlannedRow && (
            <p className="text-xs text-muted">
              {PLANNED_HINT}
              {hasBookingRow && BOOKING_HINT}
            </p>
          )}
          {rows.map(({ key, item: it }, i) => {
            const share = state.targetAmounts[key];
            // D-M5: a schedule refund row carries the stored sign; the plan is its magnitude
            const remainingOwn = Math.abs(it.amount_minor) - it.settled_minor;
            // Whenever the share differs from the plan, in the account's currency. A planned row closes at what was
            // paid, so it also says what that does to its plan; a booking row is paid in full (§3.4) and has no outcome.
            const c = comparisons.get(key);
            const diff =
              c && isHighlighted(c)
                ? isPlanned(key)
                  ? outcomeText(
                      c,
                      settleOutcome({ ...c, mode: 'restate', settledSoFarMinor: it.settled_minor }),
                      draftCurrency,
                      it.currency,
                      draftCurrency,
                    )
                  : planLine(c, draftCurrency)
                : null;
            return (
              <div
                key={key}
                className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border border-[var(--border-hairline)] px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  {/* Full `categories`, not `activeCategories`: a target's own
                      article can be a deactivated one. */}
                  <p className="truncate font-medium text-foreground">{categoryName(it.category_id)}</p>
                  <p className="text-xs text-muted">
                    Plan {formatMinor(remainingOwn, it.currency)}
                    {it.currency !== currency ? ` (in ${it.currency})` : ''}
                  </p>
                  {diff && (
                    <p className="text-xs font-medium text-[var(--ollio-gold-deep)]" data-testid={`batch-row-diff-${i + 1}`}>
                      {diff}
                    </p>
                  )}
                </div>
                <div className="w-40 shrink-0">
                  <MoneyInput
                    label={`Amount for target ${i + 1}`}
                    currency={currency || 'EUR'}
                    value={share?.major ?? ''}
                    onChange={(major) => dispatch({ type: 'editTargetAmount', target: key, major })}
                    error={share && staleFieldError(share, state.context.currency)}
                    disabled={!context}
                  />
                </div>
              </div>
            );
          })}
        </fieldset>

        <DraftLinesEditor
          state={state}
          dispatch={dispatch}
          currency={currency}
          kinds={incoming ? INCOMING_KINDS : OUTGOING_KINDS}
          articlesFor={(kind) => categoriesFor(kind, activeCategories)}
          allCategories={categories}
          disabled={!context}
          lockedSources={['shortfall']}
        />

        {/* The bank amount (OH-212): derived from the payments and the lines, read-only for an outgoing batch; for an incoming
            one the operator may type what the account ended up with, and a lower amount becomes the automatic Fee line. While
            the text is theirs it stays in the field (bankText). MoneyInput forwards no focus or input events, hence the wrapper. */}
        <div
          onFocusCapture={() => setBankFocused(true)}
          onBlurCapture={() => setBankFocused(false)}
          onInputCapture={readBankInput}
          onChangeCapture={readBankInput}
        >
          <MoneyInput
            label="Bank amount"
            currency={currency || 'EUR'}
            value={incoming && bankText !== null ? bankText : derivedMinor === null ? '' : fromMinor(derivedMinor, draftCurrency)}
            onChange={onBankChange}
            hint={incoming ? INCOMING_BANK_HINT : OUTGOING_BANK_HINT}
            error={staleFieldError(state.bank, draftCurrency)}
            disabled={!incoming || !context || !suggestionIsCurrent(state)}
          />
        </div>

        {summaryLines.length > 0 && (
          <div className="rounded-[var(--radius-card)] bg-[var(--surface-sunken)] px-4 py-3 text-sm" data-testid="batch-summary">
            {summaryLines.map((line) => (
              <p key={line} className="tabular-nums">
                {line}
              </p>
            ))}
          </div>
        )}

        <DraftSuggestionInfo state={state} preview={preview} />

        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Receipts</p>
          {/* Always TARGETLESS (Ruling T17-4; this component only runs
              under finance.confirm): the receipt stays unregistered until the
              batch's own settle registers and binds it to the movement it
              creates (bindBatchAttachments → bindVerifiedAttachmentsTx). Not
              staged against one of the rows: a row can leave the batch while
              it is open (settled elsewhere — the queue drops it from
              `items`), and a receipt staged against it would fail the whole
              batch at bind time. */}
          <ReceiptUploadButton
            target={{}}
            onUploaded={(id, file) => {
              setUploadError(null);
              setReceipts((rs) => [...rs, { id, name: file.name }]);
            }}
            onError={setUploadError}
          />
          <AttachmentList
            className="mt-2 flex flex-col gap-1"
            items={receipts}
            onRemove={(id) => setReceipts((rs) => rs.filter((r) => r.id !== id))}
          />
          {uploadError && (
            <p role="alert" className="mt-1 text-sm text-destructive">
              {uploadError}
            </p>
          )}
        </div>

        {problems.length > 0 && (
          <ul className="flex flex-col gap-0.5 text-sm text-[var(--ollio-gold-deep)]" data-testid="batch-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={settle.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant={twice.armed ? 'gold' : 'primary'} disabled={!canConfirm} loading={settle.isPending}>
            {twice.armed ? `Confirm — ${offPlanText(pressing)}` : 'Confirm'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
